import {
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { grey } from '@mui/material/colors';
import DeleteIcon from '@mui/icons-material/Delete';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useContext, useState } from 'react';
import UserContext from '../../../shared-components/contexts/UserContext';
import DeleteConfirmation from '../../../shared-components/DeleteConfirmation';
import { INotation } from '../../../shared-interfaces/Notation/INotation';
import { INotationComment } from '../../../shared-interfaces/Notation/INotationComment';
import { IMethodOfContact } from '../../../shared-interfaces/Notation/IMethodOfContact';
import { GetCoreUserFormattedName } from '../../../shared-interfaces/User/ICoreUser';
import AlertService from '../../../shared-services/Notation/AlertService';
import DataInsightService from '../../../shared-services/Notation/DataInsightService';
import UpdateNoteService from '../../../shared-services/Notation/UpdateNoteService';

export interface IResponseListItem {
  comment: INotationComment;
  methodOfContact?: IMethodOfContact;
  notation: INotation;
  afterDelete: (commentId: number) => void;
}

const useStyles = makeStyles(theme => ({
  avatar: {
    width: '60px',
    height: '60px',
    marginRight: '16px',
  },
  row: {
    borderBottom: '1px solid #dcdcdc',
  },
  listItemText: {
    [theme.breakpoints.only('xs')]: {
      maxWidth: '70%',
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: '100%',
    },
    paddingRight: '8px',
  },
  listItemIcon: {
    minWidth: '38px',
  },
  listItemRight: {
    marginTop: '8px',
    minWidth: '160px',
  },
  caption: {
    fontStyle: 'italic',
    color: '#666',
  },
  detailContainer: {
    '& p': {
      margin: 0,
    },
    marginBottom: '8px',
  },
  greyIcon: {
    color: grey[500],
  },
}));

export const ResponseListItemLoadingSkeleton = () => {
  const classes = useStyles();
  return (
    <ListItem aria-busy="true" className={classes.row} alignItems="flex-start">
      <ListItemIcon className={classes.listItemIcon}>
        <Skeleton variant="rectangular" height="24px" width="24px" />
      </ListItemIcon>
      <ListItemText
        className={classes.listItemText}
        primaryTypographyProps={{ variant: 'h3' }}
        disableTypography
        primary={<Skeleton variant="text" width={200} />}
        secondary={
          <>
            <Skeleton variant="text" width={300} />
            <Skeleton variant="text" width={250} />
            <Skeleton variant="text" width={250} />
          </>
        }
      />
      <div className={classes.listItemRight}>
        <div style={{ fontWeight: 600 }}>
          <Skeleton variant="rectangular" width={24} height={24} />
        </div>
      </div>
    </ListItem>
  );
};

const ResponseListItem = (props: IResponseListItem) => {
  const classes = useStyles();
  const user = useContext(UserContext);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  //console.log('action type: ' + props.comment.methodOfContact.name);
  async function deleteComment() {
    try {
      const notationId = props.notation.id;
      const commentId = props.comment.id;
      if (notationId && commentId) {
        switch (props.notation.notationType) {
          case 'Alert':
            await AlertService.deleteComment(notationId, commentId);
            break;
          case 'Update':
            await UpdateNoteService.deleteComment(notationId, commentId);
            break;
          case 'DataInsight':
            await DataInsightService.deleteComment(notationId, commentId);
            break;
        }
        enqueueSnackbar('Deleted Response', {
          variant: 'success',
        });
        props.afterDelete(props.comment.id);
      }
    } catch {
      enqueueSnackbar('Response Deletion Failed', {
        variant: 'error',
      });
    }
    setOpenDeleteDialog(false);
  }

  return <>
    <ListItem
      aria-busy="true"
      className={classes.row}
      alignItems="flex-start"
    >
      <ListItemIcon>
        <QuestionAnswerIcon className={classes.greyIcon} />
      </ListItemIcon>
      <ListItemText
        className={classes.listItemText}
        primaryTypographyProps={{ variant: 'h3' }}
        disableTypography
        primary={
          <div>
            <span style={{ fontWeight: 600 }}>
              {GetCoreUserFormattedName(props.comment.createdBy, false)}
            </span>
            {' - '}
            {props.comment.createdOn.toLocaleDateString()} at{' '}
            {props.comment.createdOn.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        }
        secondary={
          <>
            <Typography variant="caption" className={classes.caption}>
              {props.comment.action !== null && (
                <div style={{ fontStyle: 'italic' }}>
                  Action: {props.comment.action.name}
                  {props.comment.methodOfContact !== null && (
                    <span> - {props.comment.methodOfContact.name}</span>
                  )}
                </div>
              )}
            </Typography>
            <div
              className={classes.detailContainer}
              dangerouslySetInnerHTML={{ __html: props.comment.details }}
            />
          </>
        }
      />
      {user.isAdministrator && (
        <ListItemSecondaryAction>
          <IconButton
            aria-label="delete response"
            onClick={() => {
              setOpenDeleteDialog(true);
            }}
            size="large">
            <DeleteIcon color="primary" />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </ListItem>
    <DeleteConfirmation
      title="Are you sure you want to delete this response?"
      close={() => setOpenDeleteDialog(false)}
      open={openDeleteDialog}
      onDelete={deleteComment}
    />
  </>;
};

export default ResponseListItem;
